import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import Cropper from "react-easy-crop";
import { Modal, Button, Slider } from "antd";
import upload from "../assets/Images/Uploadminimalistic.svg";
import "./upload-image.css";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { URLS } from "../API/API endpoints";
import axiosInstance from "../AxiosInstance";

// Helper: get the cropped image as a Blob from canvas
const getCroppedImg = (imageSrc, pixelCrop) => {
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.addEventListener("load", () => {
      const canvas = document.createElement("canvas");
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      const ctx = canvas.getContext("2d");

      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );

      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Canvas is empty"));
          return;
        }
        resolve(blob);
      }, "image/jpeg");
    });
    image.addEventListener("error", reject);
    image.src = imageSrc;
  });
};

// Helper: convert Blob to base64 string
const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const UploadMediaonly = ({
  onfileAdd,
  foldername,
  urlgenerated,
  multiple = false,
  setBase64Images,
  keyname = false,
  imageurl,
  setIsFileValid,
  setImageLoading,
  isCrop,
}) => {
  const { t } = useTranslation();
  const [sizeError, setSizeError] = useState(false);
  const [fileError, setfileError] = useState(false);

  // Crop modal state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState(null);
  const [rawFile, setRawFile] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const storeId = useSelector((state) => state.login?.user?.result?.company_id);

  const handleUpload = async (file, base64DataUrl) => {
    if (!storeId) {
      console.error("Store ID not available");
      return;
    }
    setImageLoading(true);
    try {
      const imageData = base64DataUrl.split(",")[1];
      const payload = {
        data: imageData,
        bucket: process.env.REACT_APP__IMAGE_BUCKET,
        folder_path: foldername,
        filename: `${storeId}-${onfileAdd}.${file.type.split("/")[1]}`,
      };

      const location = await axiosInstance.post(URLS.UPLOAD_IMAGE, payload);

      const data = {
        name: onfileAdd,
        url: location?.data?.result?.url + "?" + Date.now(),
      };

      keyname ? urlgenerated(data, keyname) : urlgenerated(data);
    } catch (error) {
      console.error("Error uploading image:", error);
    } finally {
      setImageLoading(false);
    }
  };

  // Called after user confirms crop
  const handleCropConfirm = async () => {
    try {
      const croppedBlob = await getCroppedImg(rawImageSrc, croppedAreaPixels);
      const base64DataUrl = await blobToBase64(croppedBlob);

      setBase64Images(base64DataUrl);
      setCropModalOpen(false);

      // Create a File-like object from the cropped blob for upload
      const croppedFile = new File(
        [croppedBlob],
        rawFile.name || "cropped.jpg",
        { type: "image/jpeg" }
      );

      await handleUpload(croppedFile, base64DataUrl);
    } catch (error) {
      console.error("Error cropping image:", error);
    }
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    setRawImageSrc(null);
    setRawFile(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const onDrop = useCallback(
    (acceptedFiles) => {
      acceptedFiles.forEach((file) => {
        if (file.size > 5 * 1024 * 1024) {
          setSizeError(true);
          return;
        }
        setSizeError(false);

        if (
          file.type !== "image/jpeg" &&
          file.type !== "image/jpg" &&
          file.type !== "image/png"
        ) {
          setfileError(true);
          setIsFileValid(false);
          return;
        }

        setfileError(false);
        setIsFileValid(true);

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          if (isCrop) {
            // Open crop modal with the raw image
            setRawImageSrc(reader.result);
            setRawFile(file);
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setCropModalOpen(true);
          } else {
            // Original flow — upload directly
            setBase64Images(reader.result);
            handleUpload(file, reader.result);
          }
        };
        reader.onerror = (error) => console.error("Error:", error);
      });
    },
    [isCrop, handleUpload, setBase64Images, setIsFileValid]
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/jpeg, image/png, image/jpg",
    multiple,
    onDrop,
  });

  return (
    <>
      <div className="upload">
        <div {...getRootProps()}>
          <input {...getInputProps()} />
          <p className="flex flex-col justify-end text-center text-sm text-body cursor-pointer">
            <span className="flex flex-row justify-center gap-2 items-center font-semibold text-[#1b4597]">
              {onfileAdd !== "storeuserurl" && onfileAdd !== "userphoto" && (
                <img className="ml-1" src={upload} width={18} alt="Upload Icon" />
              )}
              {imageurl === "" ? ` ${t("upload")}` : ` ${t("change")}`}
            </span>
            {onfileAdd !== "userphoto" && imageurl === "" && (
              <div>
                <span className="text-xs text-body text-[#999999]">
                  png, jpg, jpeg
                </span>
                <div className="text-xs text-body text-[#999999]">(max 5mb)</div>
              </div>
            )}
          </p>
        </div>

        {sizeError && (
          <p className="text-red text-xs mt-1">
            {t("File cannot be more than 5 mb")}
          </p>
        )}
        {fileError && (
          <p className="text-red text-xs mt-1">{t("Unsupported File Type")}</p>
        )}
      </div>

      {/* Square Crop Modal */}
      {isCrop && (
        <Modal
          open={cropModalOpen}
          title={t("Crop Image")}
          onCancel={handleCropCancel}
          footer={[
               <div
      key="cancel"
      onClick={handleCropCancel}
      className="px-4 py-2 border border-gray-300 rounded-md cursor-pointer inline-flex items-center justify-center"
    >
      {t("Cancel")}
    </div>,

            <div
      key="confirm"
      onClick={handleCropConfirm}
      className="px-4 py-2 bg-primaryBlue text-white rounded-md cursor-pointer inline-flex items-center justify-center ml-2"
    >
      {t("Crop & Upload")}
    </div>,
          ]}
          width={520}
          destroyOnClose
        >
          {rawImageSrc && (
            <div>
              {/* Cropper container */}
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: 380,
                  background: "#1a1a1a",
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              >
                <Cropper
                  image={rawImageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1} // Square crop
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>

              {/* Zoom slider */}
              <div
                style={{
                  marginTop: 16,
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <span style={{ fontSize: 12, color: "#666", minWidth: 36 }}>
                  {t("Zoom")}
                </span>
                <Slider
                  min={1}
                  max={3}
                  step={0.05}
                  value={zoom}
                  onChange={setZoom}
                  style={{ flex: 1 }}
                  tooltip={{ formatter: (v) => `${Math.round(v * 100)}%` }}
                />
              </div>
            </div>
          )}
        </Modal>
      )}
    </>
  );
};

export default UploadMediaonly;